/**
 * Pure helpers for the Dispatch filters panel: option lists and match tests
 * over the flat weekly WorkScopeAspect rows from {@link WeeklyContext}
 * (`row.job`, `row.scope`, `row.worker_assignment`, `row.date`).
 *
 * Generic option helpers (merge/effective-id logic) live in
 * `features/planning/shared/filters/planningFilterModel.js`.
 */

import { getWorkerDisplayLabel } from "../../../utils/business";

/**
 * @typedef {import("../shared/filters/planningFilterModel").PlanningFilterOption} PlanningFilterOption
 */

/**
 * Map weekly jobs to project filter options, sorted by job-number label.
 *
 * @param {Array<Object>} jobs Weekly dispatch jobs.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchProjectOptions(jobs) {
  return (jobs ?? [])
    .map((job) => ({
      value: String(job.id),
      label: `${job.number} — ${job.description}`,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true })
    );
}

/**
 * Unique work scopes on rows, sorted by title. Rows with no scope set are
 * skipped (they cannot be targeted by the scope filter).
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchScopeOptions(rows) {
  const byId = new Map();
  for (const row of rows ?? []) {
    const id = row?.scope?.id;
    if (id == null) {
      continue;
    }
    const key = String(id);
    if (byId.has(key)) {
      continue;
    }
    byId.set(key, { value: key, label: row.scope.title?.trim() || key });
  }
  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

/**
 * Unique workers assigned on rows, sorted by display name.
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @returns {PlanningFilterOption[]}
 */
export function collectDispatchWorkerOptions(rows) {
  const byId = new Map();
  for (const row of rows ?? []) {
    for (const worker of row?.worker_assignment ?? []) {
      if (worker?.id == null) {
        continue;
      }
      const key = String(worker.id);
      if (byId.has(key)) {
        continue;
      }
      byId.set(key, { value: key, label: getWorkerDisplayLabel(worker) });
    }
  }
  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

/**
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} projectIds Selected project (job) ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesProjectFilter(row, projectIds) {
  if (!projectIds?.length) {
    return true;
  }
  return projectIds.includes(String(row?.job?.id));
}

/**
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} scopeIds Selected work-scope ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesScopeFilter(row, scopeIds) {
  if (!scopeIds?.length) {
    return true;
  }
  return scopeIds.includes(String(row?.scope?.id));
}

/**
 * @param {Object} row WorkScopeAspect row.
 * @param {Array<string>} workerIds Selected worker ids; `[]` matches all.
 * @returns {boolean}
 */
export function dispatchRowMatchesWorkerFilter(row, workerIds) {
  if (!workerIds?.length) {
    return true;
  }
  return (row?.worker_assignment ?? []).some((worker) =>
    workerIds.includes(String(worker?.id))
  );
}

/**
 * Keep rows matching all active filters (AND).
 *
 * @param {Array<Object>} rows Weekly WorkScopeAspect rows.
 * @param {Object} filters
 * @param {Array<string>} [filters.projectIds]
 * @param {Array<string>} [filters.scopeIds]
 * @param {Array<string>} [filters.workerIds]
 * @returns {Array<Object>}
 */
export function filterDispatchScopeRows(rows, { projectIds, scopeIds, workerIds }) {
  return (rows ?? []).filter(
    (row) =>
      dispatchRowMatchesProjectFilter(row, projectIds) &&
      dispatchRowMatchesScopeFilter(row, scopeIds) &&
      dispatchRowMatchesWorkerFilter(row, workerIds)
  );
}
